import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDate } from '@/lib/format-date';
import ArtworkCard from '@/components/ArtworkCard';

const ArtFairsPage: React.FC = () => {
  const { t, language, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const artFairsRaw = useAdminStore((s) => s.artFairs);

  const artFairs = useMemo(
    () => [...artFairsRaw].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
    [artFairsRaw],
  );

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-20"
        >
          <h1 className="text-2xl sm:text-4xl md:text-7xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.artFairs.title}
          </h1>
        </motion.div>

        <div className="space-y-32">
          {artFairs.map((fair, i) => {
            const startDate = formatDate(fair.startDate, language);
            const endDate = formatDate(fair.endDate, language);

            return (
              <motion.div
                key={fair._id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15, duration: 0.8 }}
              >
                {/* Fair Header */}
                <Link to={`/art-fairs/${fair.slug}`} className="flex flex-col md:flex-row gap-6 sm:gap-10 mb-10 sm:mb-16 group">
                  <div className="w-full md:w-[320px] lg:w-[380px] aspect-[4/3] rounded-2xl overflow-hidden bg-[#f9f9f9] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 shrink-0">
                    <img
                      src={fair.poster}
                      alt={localize(fair.title)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-center space-y-4 sm:space-y-5">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
                      {localize(fair.title)}
                    </h3>
                    {(fair.excerpt?.en || fair.excerpt?.ka) ? (
                      <p className="text-sm sm:text-base text-black/50 dark:text-white/50 font-light leading-relaxed line-clamp-3">
                        {localize(fair.excerpt)}
                      </p>
                    ) : (
                      <p className="text-sm sm:text-base text-black/50 dark:text-white/50 font-light leading-relaxed line-clamp-3">
                        {localize(fair.description)}
                      </p>
                    )}
                    <div className="flex flex-col gap-3 text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        {startDate} &mdash; {endDate}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin size={14} />
                        {localize(fair.location)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white transition-colors">
                      {t.artFairs.viewFair} <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>

                {/* Artworks for sale at the fair */}
                {fair.artworks.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.5em] text-black/30 dark:text-white/30 mb-8">
                      Available Works
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8">
                      {fair.artworks.map((artwork) => (
                        <ArtworkCard key={artwork._id} artwork={artwork} />
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ArtFairsPage;
